import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";

import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { CsatSurvey, csatSurveyEligible } from "../components/CsatSurvey";
import { Icon } from "../components/Icon";
import {
  READ_ONLY_VIEW_AS_TITLE,
  clearRaiseTicketReturn,
  requestRaiseTicketFocus,
  takeRaiseTicketReturn,
} from "../components/RaiseTicketLink";
import { features } from "../config/features";
import { useModalDismiss } from "../hooks/useModalDismiss";
import {
  api,
  type AutotaskTicketAttachment,
  type AutotaskTicketDetail,
  type AutotaskTicketListResponse,
  type KbArticleListItem,
  type Me,
  type TicketScope,
  type TicketSortDir,
  type TicketSortKey,
} from "../services/api";
import { syncedAgo } from "../utils/syncedAgo";
import { slaPill, statusBadgeClass } from "../utils/ticketStatus";

const IQUEST_STAFF_ROLES: ReadonlyArray<string> = ["service_desk", "iquest_admin"];
const COMPANY_VIEW_PERMISSION = "view_all_company_tickets";
const PAGE_SIZE = 25;

type StatusToken =
  | ""
  | "open"
  | "in-progress"
  | "waiting"
  | "resolved"
  | "closed";
type PriorityToken = "" | "low" | "medium" | "high" | "critical";

const STATUS_OPTIONS: { value: StatusToken; label: string }[] = [
  { value: "", label: "Any status" },
  { value: "open", label: "Open" },
  { value: "in-progress", label: "In progress" },
  { value: "waiting", label: "Waiting" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const PRIORITY_OPTIONS: { value: PriorityToken; label: string }[] = [
  { value: "", label: "Any priority" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

// ---- Priority assessment (Impact x Urgency -> derived priority) -------------
// Replaces the free-pick priority dropdown in the New ticket modal: the
// requester answers two guided questions (plus an optional business-critical
// flag) and the modal derives a priority, which is sent as the `priority`
// token on create. The service desk still confirms it on triage.
interface PaOption {
  val: number;
  title: string;
  sub: string;
}

const IMPACT_OPTIONS: PaOption[] = [
  { val: 1, title: "Just me", sub: "Only my own work is affected" },
  { val: 2, title: "My team", sub: "A handful of colleagues" },
  { val: 3, title: "A site or department", sub: "A whole location or business unit" },
  { val: 4, title: "The whole company", sub: "Everyone, across all sites" },
];

const URGENCY_OPTIONS: PaOption[] = [
  { val: 1, title: "Minor annoyance", sub: "I can work as normal" },
  { val: 2, title: "Slowing me down", sub: "There's a workaround, but it's painful" },
  { val: 3, title: "Nearly blocked", sub: "No workaround; barely able to continue" },
  { val: 4, title: "Work has stopped", sub: "Completely unable to do my job" },
];

const PA_NAMES = ["Low", "Medium", "High", "Critical"] as const;
const PA_WHY = [
  "Limited impact and a workaround is available.",
  "Some disruption, handled within standard targets.",
  "Significant disruption to a group of users.",
  "Major or company-wide outage; fastest response.",
];

function derivePriority(
  impact: number,
  urgency: number,
  critical: boolean,
): { name: string; why: string } {
  const score = impact + urgency; // 2..8
  let level = score <= 3 ? 0 : score <= 5 ? 1 : score === 6 ? 2 : 3;
  if (critical) level = Math.min(level + 1, 3);
  return { name: PA_NAMES[level]!, why: PA_WHY[level]! };
}

const MAX_ATTACHMENTS = 5;
// Keep in step with the backend per-file cap (_ATTACHMENT_MAX_BYTES in
// services/tickets.py), which mirrors Autotask's ~6 MB per-file API limit.
// The old hint said "10 MB", so oversize screenshots showed as attached and
// then silently 400'd on the best-effort post-create upload (#71).
const MAX_ATTACH_MB = 6;
const MAX_ATTACH_BYTES = MAX_ATTACH_MB * 1024 * 1024;
const ATTACH_ACCEPT = "image/*,.pdf,.txt,.log,.doc,.docx";

function fmtFileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1048576).toFixed(1)} MB`;
}

// Autotask hard-caps attachments at ~6 MB (API limit, base64 on the wire), well
// below what a full-screen PNG screenshot can weigh. Rather than reject those,
// re-encode oversize images to JPEG (and downscale if needed) so they fit and
// still upload to the Autotask ticket. Diagnostic screenshots lose no useful
// legibility. Returns the shrunk File, or null if it still won't fit / can't be
// decoded (caller then rejects it with a message). #71.
async function compressImageUnder(
  file: File,
  maxBytes: number,
): Promise<File | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const base = file.name.replace(/\.[^.]+$/, "") || "screenshot";
    try {
      for (const scale of [1, 0.75, 0.5]) {
        const w = Math.max(1, Math.round(bitmap.width * scale));
        const h = Math.max(1, Math.round(bitmap.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(bitmap, 0, 0, w, h);
        for (const q of [0.85, 0.7, 0.55]) {
          const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", q),
          );
          if (blob && blob.size <= maxBytes) {
            return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
          }
        }
      }
      return null;
    } finally {
      bitmap.close();
    }
  } catch {
    return null;
  }
}

// Pass a file through if it is under the cap, shrink an oversize image to fit,
// or return null (caller rejects it). Shared by the new-ticket form and the
// reply box on an existing ticket, so both accept the same screenshots (#191).
export async function fitUnderAttachCap(file: File): Promise<File | null> {
  if (file.size <= MAX_ATTACH_BYTES) return file;
  if (file.type.startsWith("image/") && file.type !== "image/gif") {
    return compressImageUnder(file, MAX_ATTACH_BYTES);
  }
  return null;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

function fmtAge(hours: number | null): string {
  if (hours === null || hours === undefined) return "";
  if (hours < 1) {
    const m = Math.max(1, Math.round(hours * 60));
    return `${m}m`;
  }
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

function priorityClass(label: string | null): string {
  if (!label) return "";
  const l = label.toLowerCase();
  if (l.includes("critical")) return "priority-critical";
  if (l.includes("high")) return "priority-high";
  if (l.includes("medium")) return "priority-medium";
  if (l.includes("low")) return "priority-low";
  return "priority-medium";
}

// ---- Column sorting ---------------------------------------------------------

type TicketSort = { key: TicketSortKey; dir: TicketSortDir };

// Which way a column sorts on its FIRST click, so one click lands on the thing
// people actually want: names A-Z, but most urgent and longest-open first.
const FIRST_CLICK_DIR: Record<TicketSortKey, TicketSortDir> = {
  title: "asc",
  requester: "asc",
  company: "asc",
  assignee: "asc",
  status: "asc",
  priority: "desc",
  age: "desc",
};

/** One sortable column heading. The button (not the `th`) is the control so it
 *  is reachable by keyboard, and `aria-sort` announces the current order. */
function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: TicketSortKey;
  sort: TicketSort | null;
  onSort: (key: TicketSortKey) => void;
}) {
  const active = sort?.key === sortKey;
  return (
    <th
      className={`th-sortable${active ? " is-sorted" : ""}`}
      aria-sort={
        active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button type="button" className="th-sort-btn" onClick={() => onSort(sortKey)}>
        {label}
        <span className="th-sort-arrow" aria-hidden="true">
          {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

function csatClass(temperature: string | null): string {
  if (!temperature) return "";
  const t = temperature.toLowerCase();
  if (t.includes("gold")) return "csat-gold";
  if (t.includes("lukewarm")) return "csat-lukewarm";
  if (t.includes("cold")) return "csat-cold";
  return "csat-lukewarm";
}

function availableScopes(me: Me | undefined): TicketScope[] {
  if (!me) return ["my"];
  const isStaff = IQUEST_STAFF_ROLES.includes(me.role);
  const isAdmin = me.role === "client_admin";
  const hasCompanyView =
    isStaff || isAdmin || (me.permissions ?? []).includes(COMPANY_VIEW_PERMISSION);
  const scopes: TicketScope[] = ["my"];
  if (hasCompanyView) scopes.push("company");
  if (isStaff) scopes.push("all");
  return scopes;
}

function defaultScope(me: Me | undefined): TicketScope {
  if (!me) return "my";
  if (IQUEST_STAFF_ROLES.includes(me.role)) return "all";
  if (me.role === "client_admin") return "company";
  if ((me.permissions ?? []).includes(COMPANY_VIEW_PERMISSION)) return "company";
  return "my";
}

function scopeLabel(scope: TicketScope): string {
  if (scope === "my") return "My tickets";
  if (scope === "company") return "All company tickets";
  return "All tickets";
}

/** A `?scope=` query value, validated against the three real scopes. */
function parseScopeParam(raw: string | null): TicketScope | null {
  return raw === "my" || raw === "company" || raw === "all" ? raw : null;
}

/** A `?status=` query value, validated against the status filter tokens. */
function parseStatusParam(raw: string | null): StatusToken {
  return STATUS_OPTIONS.some((o) => o.value === raw) ? (raw as StatusToken) : "";
}

function scopeSubtitle(scope: TicketScope): string {
  if (scope === "my") {
    return "Tickets where you are the requester.";
  }
  if (scope === "company") {
    return "Every ticket open against your company.";
  }
  return "Every ticket across every iQuest client.";
}

export function Tickets() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const { ticketNumber: routeTicketNumber } = useParams<{ ticketNumber?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scopes = useMemo(() => availableScopes(me), [me]);
  // Initial scope/status are seeded from the URL so the Dashboard pills can
  // deep-link into a filtered list (e.g. /tickets?scope=company&status=open).
  // Read once at mount; the user is then free to switch tabs/filters.
  const [scope, setScope] = useState<TicketScope | null>(() =>
    parseScopeParam(searchParams.get("scope")),
  );
  const activeScope: TicketScope = scope ?? defaultScope(me);
  const [statusFilter, setStatusFilter] = useState<StatusToken>(() =>
    parseStatusParam(searchParams.get("status")),
  );
  const [priorityFilter, setPriorityFilter] = useState<PriorityToken>("");
  // Column sort. null = the server's newest-first default, which is why no
  // heading starts with an arrow. Session-only by design (issue #146): it is not
  // seeded from or written back to the URL, so a reload returns to newest-first.
  const [sort, setSort] = useState<TicketSort | null>(null);
  // Search input is immediate; `search` (fed to the query key) trails it by a
  // 300ms debounce so we don't fire an API call per keystroke (KB.tsx pattern).
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedTicketNumber, setSelectedTicketNumber] = useState<string | null>(
    routeTicketNumber ?? null,
  );
  // `?new=1` (from the dashboard "Raise a ticket" quick action and the Service
  // Catalog "Can't find the right form?" CTA) opens the create modal on mount.
  const [showCreate, setShowCreate] = useState(
    () => searchParams.get("new") === "1" || searchParams.get("new") === "true",
  );
  // The control that opened the form on this page (the New ticket button).
  // The quick-action links live on other routes; those are tracked separately
  // so a dismiss can return focus to them.
  const newTicketRef = useRef<HTMLButtonElement>(null);
  const createOpenerRef = useRef<HTMLElement | null>(null);
  const pendingLocalFocus = useRef<HTMLElement | null>(null);
  const impersonating = !!me?.impersonating;

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // /tickets/:ticketNumber opens the detail modal; keep it in sync so a fresh
  // deep-link (e.g. clicking another recent ticket) reopens for the new one.
  useEffect(() => {
    if (routeTicketNumber) setSelectedTicketNumber(routeTicketNumber);
  }, [routeTicketNumber]);

  // A URL-seeded scope the caller can't actually access (manually typed, or a
  // permission since revoked) falls back to their default once `me` loads.
  useEffect(() => {
    if (scope && me && !scopes.includes(scope)) setScope(null);
  }, [scope, me, scopes]);

  // View-as is read-only. A deep link must not open the form, same as the
  // disabled New ticket button.
  useEffect(() => {
    if (!impersonating) return;
    if (!showCreate && !searchParams.has("new")) return;
    clearRaiseTicketReturn();
    if (showCreate) setShowCreate(false);
    if (searchParams.has("new")) {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [impersonating, showCreate, searchParams, setSearchParams]);

  // After the modal unmounts, its own focus restore may have landed on the
  // body. Focus the opener on the next turn, after that cleanup.
  useEffect(() => {
    if (showCreate) return;
    const opener = pendingLocalFocus.current;
    if (!opener) return;
    const id = window.setTimeout(() => {
      pendingLocalFocus.current = null;
      if (opener.isConnected) opener.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [showCreate]);

  // Closing the detail clears local state, and — when we arrived via
  // /tickets/:ticketNumber — drops back to the bare list so the URL is clean
  // and the same ticket can be reopened.
  function closeDetail() {
    setSelectedTicketNumber(null);
    if (routeTicketNumber) navigate("/tickets", { replace: true });
  }

  function stripNewParam() {
    if (!searchParams.has("new")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }

  function openCreate(opener: HTMLElement | null) {
    // A button on this page is a new gesture. Drop any quick-action return
    // so dismissing later focuses this opener, not the previous page.
    clearRaiseTicketReturn();
    createOpenerRef.current = opener;
    setShowCreate(true);
  }

  // Dismiss (Escape, Close, Cancel, backdrop). Creating a ticket stays on
  // this page; see onCreated. A quick action that navigated here is sent
  // back so focus can land on that link.
  function dismissCreate() {
    const back = takeRaiseTicketReturn();
    const opener = createOpenerRef.current ?? newTicketRef.current;
    createOpenerRef.current = null;
    setShowCreate(false);
    if (back) {
      requestRaiseTicketFocus(back.key);
      navigate(back.path, { replace: true });
      return;
    }
    stripNewParam();
    pendingLocalFocus.current = opener;
  }

  function closeAfterCreate() {
    clearRaiseTicketReturn();
    createOpenerRef.current = null;
    pendingLocalFocus.current = null;
    setShowCreate(false);
    stripNewParam();
  }

  // Switching scope changes the visible ticket set entirely; carrying the old
  // status/priority/search filters across would show a confusing stale view.
  // The sort resets too — Requester and Company only exist in some scopes, so a
  // sort could otherwise survive on a column that is no longer on screen.
  function changeScope(s: TicketScope) {
    setScope(s);
    setStatusFilter("");
    setPriorityFilter("");
    setSearchInput("");
    setSearch("");
    setSort(null);
  }

  // Click a heading to sort by it; click the same one again to reverse. The
  // request goes to the server, which orders every matching ticket before
  // paging — sorting here would only reorder the pages already loaded.
  function toggleSort(key: TicketSortKey) {
    setSort((current) =>
      current?.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: FIRST_CLICK_DIR[key] },
    );
  }

  // "Load more" paging via useSWRInfinite: each page appends the next
  // PAGE_SIZE newest rows. The first-page key encodes scope + filters, so
  // changing any of them resets the loaded size back to one page
  // (persistSize defaults to false). The page number rides on the key so the
  // fetcher can read it without threading state.
  const getKey = (
    pageIndex: number,
    previous: AutotaskTicketListResponse | null,
  ): string | null => {
    if (!me) return null;
    // Stop requesting once a page comes back short — there's nothing after it.
    if (previous && previous.items.length < PAGE_SIZE) return null;
    // The sort rides on the key so changing it resets the loaded size back to
    // one page (persistSize is false) — the new order starts from the top
    // instead of appending onto rows ordered the old way.
    return `/api/tickets|${activeScope}|${statusFilter}|${priorityFilter}|${search}|${sort?.key ?? ""}|${sort?.dir ?? ""}|${pageIndex + 1}`;
  };

  const {
    data: pages,
    error,
    size,
    setSize,
    mutate,
  } = useSWRInfinite<AutotaskTicketListResponse>(getKey, (k: string) =>
    api.tickets.list({
      scope: activeScope,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      q: search || undefined,
      sort: sort?.key,
      dir: sort ? sort.dir : undefined,
      page: Number(k.split("|").pop()),
      page_size: PAGE_SIZE,
    }),
  );

  const items = pages ? pages.flatMap((p) => p.items) : [];
  // `count` is the total in the visibility window and is stable across pages.
  const total = pages?.[0]?.count ?? 0;
  // Present only when the list is served from the cache (else undefined).
  const syncedAt = pages?.[0]?.synced_at ?? null;
  const hasMore = items.length < total;
  const isLoadingInitial = !pages && !error;
  const isLoadingMore =
    !!pages && size > 0 && typeof pages[size - 1] === "undefined";

  const showRequester = activeScope !== "my";
  const showCompany = activeScope === "all";
  // Fixed columns: Title, Priority, Status, SLA, Assignee, Age (6) plus the
  // two optional ones. Derived so the empty/loading/error colSpan can never
  // drift from the header.
  const columnCount = 6 + (showRequester ? 1 : 0) + (showCompany ? 1 : 0);

  return (
    <>
      <div className="page-header page-header-with-action">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">{scopeSubtitle(activeScope)}</p>
        </div>
        <button
          ref={newTicketRef}
          type="button"
          className="btn btn-primary"
          onClick={(e) => openCreate(e.currentTarget)}
          disabled={impersonating}
          title={impersonating ? READ_ONLY_VIEW_AS_TITLE : undefined}
        >
          New ticket
        </button>
      </div>

      {scopes.length > 1 && (
        <div className="scope-tabs" role="tablist">
          {scopes.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={activeScope === s}
              className={`scope-tab ${activeScope === s ? "active" : ""}`}
              onClick={() => changeScope(s)}
            >
              {scopeLabel(s)}
            </button>
          ))}
        </div>
      )}

      <div className="ticket-filter-row">
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusToken)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as PriorityToken)}
        >
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search title, description or ticket number"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh label="Title" sortKey="title" sort={sort} onSort={toggleSort} />
                {showRequester && (
                  <SortableTh
                    label="Requester"
                    sortKey="requester"
                    sort={sort}
                    onSort={toggleSort}
                  />
                )}
                {showCompany && (
                  <SortableTh
                    label="Company"
                    sortKey="company"
                    sort={sort}
                    onSort={toggleSort}
                  />
                )}
                <SortableTh
                  label="Priority"
                  sortKey="priority"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                {/* SLA is not sortable: the pill is computed per request from
                    the due dates + the AU holiday calendar, so there is no
                    column to order on. Sorting it by response-due instead would
                    put an arrow on a heading it does not actually track. */}
                <th>SLA</th>
                <SortableTh
                  label="Assignee"
                  sortKey="assignee"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh label="Age" sortKey="age" sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {isLoadingInitial && (
                <tr>
                  <td className="ticket-empty" colSpan={columnCount}>
                    Loading tickets...
                  </td>
                </tr>
              )}
              {error && !isLoadingInitial && (
                <tr>
                  <td className="ticket-empty" colSpan={columnCount}>
                    {String((error as Error).message ?? error)}
                  </td>
                </tr>
              )}
              {!isLoadingInitial && !error && items.length === 0 && (
                <tr>
                  <td className="ticket-empty" colSpan={columnCount}>
                    No tickets match your filters.
                  </td>
                </tr>
              )}
              {!isLoadingInitial &&
                !error &&
                items.map((t, i) => (
                  <tr
                    key={t.id ?? t.ticketNumber ?? `row-${i}`}
                    className="ticket-table-row"
                    onClick={() => t.ticketNumber && setSelectedTicketNumber(t.ticketNumber)}
                  >
                    <td>
                      <strong>{t.title ?? "(no title)"}</strong>
                      {t.ticketNumber && (
                        <div className="form-field-meta" style={{ marginTop: 2 }}>
                          <span className="form-counter">{t.ticketNumber}</span>
                        </div>
                      )}
                    </td>
                    {showRequester && <td>{t.requester_name ?? "—"}</td>}
                    {showCompany && <td>{t.company_name ?? ""}</td>}
                    <td>
                      <span className={`priority-dot ${priorityClass(t.priority_label)}`} />{" "}
                      {t.priority_label ?? ""}
                    </td>
                    <td>
                      <span className={statusBadgeClass(t.status_label)}>
                        {t.status_label ?? "unknown"}
                      </span>
                      {t.csat_temperature && (
                        <span
                          className={`csat-panel-icon ${csatClass(t.csat_temperature)}`}
                          style={{ marginLeft: 8 }}
                        >
                          {t.csat_temperature}
                        </span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const pill = slaPill(t.sla_posture);
                        return pill ? (
                          <span className={pill.cls}>{pill.label}</span>
                        ) : null;
                      })()}
                    </td>
                    <td>{t.assignee_name ?? "Unassigned"}</td>
                    <td>{fmtAge(t.age_hours)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!isLoadingInitial && !error && items.length > 0 && (
          <div
            className="ticket-list-footer"
            style={{
              display: "flex",
              gap: 12,
              marginTop: 16,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {hasMore && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void setSize(size + 1)}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? "Loading…" : `Load ${PAGE_SIZE} more`}
              </button>
            )}
            <span style={{ fontSize: 13, color: "var(--iq-text-muted)" }}>
              Showing {items.length} of {total}
              {syncedAt && <> · {syncedAgo(syncedAt)}</>}
            </span>
          </div>
        )}
      </div>

      {selectedTicketNumber && (
        <TicketDetail
          ticketNumber={selectedTicketNumber}
          onClose={closeDetail}
          onNewTicket={() => {
            closeDetail();
            openCreate(null);
          }}
        />
      )}

      {showCreate && !impersonating && (
        <CreateTicketModal
          canAttach={me?.can_attach !== false}
          onClose={dismissCreate}
          onCreated={async (ticketNumber) => {
            closeAfterCreate();
            await mutate();
            if (ticketNumber) setSelectedTicketNumber(ticketNumber);
          }}
        />
      )}
    </>
  );
}

export function CreateTicketModal({
  canAttach,
  onClose,
  onCreated,
}: {
  canAttach: boolean;
  onClose: () => void;
  onCreated: (ticketNumber: string | null) => void | Promise<void>;
}) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  // Guided priority: Impact (1-4) x Urgency (1-4) + a business-critical flag.
  const [impact, setImpact] = useState<number | null>(null);
  const [urgency, setUrgency] = useState<number | null>(null);
  const [critical, setCritical] = useState(false);
  const [paTouched, setPaTouched] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  // True while addFiles() is re-encoding an oversize image (keeps submit from
  // firing before a screenshot has been shrunk and queued).
  const [processingFiles, setProcessingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once the user tries to submit without a subject, so the empty-subject
  // error shows inline under the field (mirrors the priority block's paTouched).
  const [subjectTouched, setSubjectTouched] = useState(false);
  // Once the ticket exists, `createdNumber` guards against a duplicate create
  // if an attachment upload failed (the ticket survives; the user can retry the
  // upload or open the ticket).
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  const [attachWarning, setAttachWarning] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // KB deflection: suggest articles matching the subject as the user types,
  // debounced. Reuses the existing KB search (q + small page size). Off when
  // the KB is hidden, because the article route is not registered then.
  const [suggestQuery, setSuggestQuery] = useState("");
  const panelRef = useModalDismiss(onClose);
  useEffect(() => {
    const id = setTimeout(() => setSuggestQuery(subject.trim()), 350);
    return () => clearTimeout(id);
  }, [subject]);
  const { data: suggestions } = useSWR<KbArticleListItem[]>(
    features.kb && suggestQuery.length >= 3 ? ["/api/kb/suggest", suggestQuery] : null,
    async () => (await api.kb.list({ q: suggestQuery, page_size: 5 })).items,
  );

  const derived =
    impact !== null && urgency !== null
      ? derivePriority(impact, urgency, critical)
      : null;
  const subjectMissing = subjectTouched && !subject.trim();

  // Vet files before the ticket is created so the user gets immediate feedback
  // rather than a silent post-create upload failure (the backend hard-rejects
  // anything over Autotask's ~6 MB cap). Oversize images are auto-shrunk to
  // fit; anything else over the cap is rejected with a specific reason. #71.
  async function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    setProcessingFiles(true);
    try {
      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const f of Array.from(list)) {
        const fitted = await fitUnderAttachCap(f);
        if (fitted) {
          accepted.push(fitted);
        } else {
          rejected.push(f.name);
        }
      }
      setAttachWarning(
        rejected.length > 0
          ? `${rejected.length} file${rejected.length > 1 ? "s" : ""} over the ${MAX_ATTACH_MB} MB limit ${
              rejected.length > 1 ? "were" : "was"
            } not added: ${rejected.join(", ")}. Please compress or split ${
              rejected.length > 1 ? "them" : "it"
            }.`
          : null,
      );
      if (accepted.length > 0) {
        setFiles((prev) => [...prev, ...accepted].slice(0, MAX_ATTACHMENTS));
      }
    } finally {
      setProcessingFiles(false);
    }
  }

  async function submit() {
    if (!subject.trim()) {
      setSubjectTouched(true);
      return;
    }
    if (impact === null || urgency === null) {
      setPaTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    setAttachWarning(null);
    try {
      let ticketNumber = createdNumber;
      if (!ticketNumber) {
        const { name } = derivePriority(impact, urgency, critical);
        const created = await api.tickets.create({
          subject: subject.trim(),
          description: description.trim() || undefined,
          priority: name.toLowerCase(),
        });
        // Prefer the human ticket number, but fall back to the numeric id when
        // the post-create hydrate didn't return one: both the attachment and
        // detail routes accept a numeric id. Without this a null ticketNumber
        // silently skipped every attachment upload with no warning (#71).
        ticketNumber =
          created.ticketNumber ??
          (created.id != null ? String(created.id) : null);
        setCreatedNumber(ticketNumber);
      }
      // Attachments are a best-effort follow-up POST: the ticket is already
      // created, so a failed upload (e.g. attachments disabled for this
      // company, or an oversize file) must never discard it. Failures keep the
      // modal open with a note and leave the failed files for a retry.
      const failed: File[] = [];
      if (ticketNumber && files.length > 0) {
        for (const f of files) {
          try {
            await api.tickets.uploadAttachment(ticketNumber, f);
          } catch {
            failed.push(f);
          }
        }
      }
      if (failed.length > 0) {
        setFiles(failed);
        setAttachWarning(
          `Ticket ${ticketNumber ?? "was created"}, but ${failed.length} attachment${
            failed.length > 1 ? "s" : ""
          } couldn't be uploaded. Retry, or open the ticket to add ${
            failed.length > 1 ? "them" : "it"
          } there.`,
        );
        setSubmitting(false);
        return;
      }
      await onCreated(ticketNumber);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create ticket.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-panel modal-panel-md" ref={panelRef}>
        <header className="modal-header">
          <h2 className="modal-title">New ticket</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label className="form-label" htmlFor="new-ticket-subject">
              Subject
            </label>
            <input
              id="new-ticket-subject"
              className={`form-input${subjectMissing ? " is-error" : ""}`}
              type="text"
              maxLength={255}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              aria-invalid={subjectMissing}
            />
            {subjectMissing && (
              <div className="iq-field-error">
                <Icon name="alert" size={13} />
                Subject is required.
              </div>
            )}
          </div>

          {suggestions && suggestions.length > 0 && (
            <div className="kb-suggest">
              <div className="kb-suggest-label">
                Before you file — these articles might help:
              </div>
              <ul className="kb-suggest-list">
                {suggestions.map((a) => (
                  <li key={a.id}>
                    <Link to={`/kb/article/${a.id}`} target="_blank" rel="noreferrer">
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="new-ticket-description">
              Description
            </label>
            <textarea
              id="new-ticket-description"
              className="form-input"
              rows={6}
              maxLength={20_000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's happening? Include any relevant detail."
            />
          </div>

          <div className="pa-block">
            <div className="pa-block-head">
              <div className="pa-block-title">Help us set the right priority</div>
              <div className="pa-block-sub">
                Answer a couple of quick questions and we&apos;ll set a starting
                priority. The service desk confirms it on triage.
              </div>
            </div>
            <PaQuestion
              tag="Impact"
              label="How many people does this affect?"
              options={IMPACT_OPTIONS}
              value={impact}
              onSelect={setImpact}
            />
            <PaQuestion
              tag="Urgency"
              label="How much is it disrupting work?"
              options={URGENCY_OPTIONS}
              value={urgency}
              onSelect={setUrgency}
            />
            <button
              type="button"
              className={`pa-flag${critical ? " selected" : ""}`}
              aria-pressed={critical}
              onClick={() => setCritical((c) => !c)}
            >
              <span className="pa-flag-box">
                <Icon name="check" size={11} />
              </span>
              <span>
                <span className="pa-flag-t">
                  This affects a business-critical or safety system
                </span>
                <span className="pa-flag-s">
                  e.g. payroll run, production line, building access. Bumps the
                  priority up a level.
                </span>
              </span>
            </button>
            <div className="form-group" style={{ marginBottom: 6 }}>
              <div
                className={`pa-result${derived ? "" : " is-empty"}${
                  paTouched && !derived ? " is-error" : ""
                }`}
              >
                <span
                  className={`pa-result-dot${derived ? ` ${priorityClass(derived.name)}` : ""}`}
                />
                <div className="pa-result-body">
                  <div className="pa-result-label">
                    {derived ? "Calculated priority" : "Priority"}
                  </div>
                  <div className="pa-result-value">
                    {derived
                      ? derived.name
                      : paTouched
                        ? "Please answer both questions to set a priority"
                        : "Answer both questions above"}
                  </div>
                  {derived && <div className="pa-result-why">{derived.why}</div>}
                </div>
              </div>
            </div>
          </div>

          {canAttach && (
          <div className="form-group">
            <label className="form-label">
              Evidence &amp; attachments{" "}
              <span className="form-optional">OPTIONAL</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACH_ACCEPT}
              style={{ display: "none" }}
              onChange={(e) => {
                void addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <div
              className="iq-filedrop"
              role="button"
              tabIndex={0}
              aria-disabled={files.length >= MAX_ATTACHMENTS}
              onClick={() => files.length < MAX_ATTACHMENTS && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  files.length < MAX_ATTACHMENTS
                ) {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                void addFiles(e.dataTransfer.files);
              }}
              style={files.length >= MAX_ATTACHMENTS ? { opacity: 0.5, pointerEvents: "none" } : undefined}
            >
              <Icon name="upload" size={22} style={{ marginBottom: 6, color: "var(--iq-text-muted)" }} />
              <div>
                Drag screenshots or files here, or <span className="link">browse</span>
              </div>
              <div style={{ fontSize: 12.5, marginTop: 4 }}>
                {processingFiles
                  ? "Optimising image…"
                  : `Screenshots, error messages, logs or photos help us diagnose faster. Large images are optimised automatically. Up to ${MAX_ATTACHMENTS} files, ${MAX_ATTACH_MB} MB each.`}
              </div>
            </div>
            {files.map((f, i) => (
              <div className="iq-file-row" key={`${f.name}-${f.size}-${i}`}>
                <Icon name="file" size={16} style={{ color: "var(--iq-violet)", flexShrink: 0 }} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.name}
                </span>
                <span className="row-meta" style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                  {fmtFileSize(f.size)}
                </span>
                <button
                  type="button"
                  className="iq-fx"
                  aria-label="Remove file"
                  disabled={submitting}
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Icon name="x" size={15} />
                </button>
              </div>
            ))}
          </div>
          )}

          {attachWarning && (
            <div className="form-error" role="status" style={{ marginBottom: 12 }}>
              {attachWarning}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {createdNumber ? (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => void onCreated(createdNumber)}
                  disabled={submitting}
                >
                  Open ticket
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void submit()}
                  disabled={submitting || processingFiles}
                >
                  {submitting ? "Uploading…" : "Retry upload"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void submit()}
                  disabled={submitting || processingFiles}
                >
                  {submitting ? "Creating…" : "Create ticket"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One guided priority question: a mono tag, a label, and a 2x2 grid of option
 * buttons (radio semantics). Selecting one lifts the answer to the modal.
 */
function PaQuestion({
  tag,
  label,
  options,
  value,
  onSelect,
}: {
  tag: string;
  label: string;
  options: PaOption[];
  value: number | null;
  onSelect: (v: number) => void;
}) {
  return (
    <div className="pa-q">
      <div className="pa-q-label">
        <span className="pa-q-tag">{tag}</span>
        {label}
      </div>
      <div className="pa-opts">
        {options.map((o) => (
          <button
            key={o.val}
            type="button"
            className={`pa-opt${value === o.val ? " selected" : ""}`}
            aria-pressed={value === o.val}
            onClick={() => onSelect(o.val)}
          >
            <span className="pa-opt-radio" />
            <span className="pa-opt-text">
              <span className="pa-opt-t">{o.title}</span>
              <span className="pa-opt-s">{o.sub}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

type TimelineEntry = {
  key: string;
  kind: "note" | "work";
  at: string | null;
  who: string;
  title: string | null;
  body: string;
  hours: number | null;
};

// Notes and time entries are two views of the same ticket history; collate them
// into one chronological feed (first action -> last) so the client reads the
// story end to end instead of two disjoint lists. Undated rows sink to the end.
function buildTimeline(data: AutotaskTicketDetail): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  data.notes.forEach((n, i) => {
    entries.push({
      key: `note-${n.id ?? i}`,
      kind: "note",
      at: n.created_at,
      who: n.created_by ?? "iQuest",
      title: n.title,
      body: n.description ?? "",
      hours: null,
    });
  });
  data.time_entries.forEach((te, i) => {
    entries.push({
      key: `work-${te.id ?? i}`,
      kind: "work",
      at: te.date_worked,
      who: te.resource_name ?? "iQuest",
      title: null,
      body: te.summary ?? "",
      hours: te.hours,
    });
  });
  const ts = (at: string | null) => {
    const t = at ? new Date(at).getTime() : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return entries.sort((a, b) => ts(a.at) - ts(b.at));
}

/** The ticket thread modal.
 *
 * Exported because the reporting drill-down opens the same modal over itself
 * rather than navigating the customer away to the Tickets page (issue #91). It
 * is self-contained — its own backdrop, its own fetch, no router dependency —
 * so it can be rendered from anywhere. */
export function TicketDetail({
  ticketNumber,
  onClose,
  onNewTicket,
}: {
  ticketNumber: string;
  onClose: () => void;
  /** Close this detail and open the New ticket flow (offered on closed tickets,
   * which can no longer be reopened). */
  onNewTicket?: () => void;
}) {
  const { data, isLoading, error, mutate } = useSWR<AutotaskTicketDetail>(
    `/api/tickets/${ticketNumber}`,
    () => api.tickets.get(ticketNumber),
  );
  const panelRef = useModalDismiss(onClose);

  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState<
    null | "note" | "attach" | "reopen" | "resolve" | "close"
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Quick-jump target: the reply box sits below the (often long) history
  // timeline, so a top-of-modal button scrolls straight to it (issue #13).
  const replyRef = useRef<HTMLDivElement | null>(null);
  const replyInputRef = useRef<HTMLTextAreaElement | null>(null);

  const caps = data?.capabilities;
  const canNote = !!caps?.can_add_note;
  const canAttach = !!caps?.can_attach;
  const statusActions = caps?.allowed_status_actions ?? [];
  // Belt-and-suspenders button visibility. The server is authoritative
  // (change_status re-checks every gate), but never surface Reopen/Close unless
  // the ticket is actually in the Resolved holding state, nor Mark-as-resolved
  // on an already-closed ticket — even if a stale or not-yet-updated backend
  // still lists the action (e.g. staging pointing at an older prod API). Keyed
  // off the semantic is_resolved / is_closed flags on the detail.
  const canReopen = statusActions.includes("reopen") && !!data?.is_resolved;
  const canClose = statusActions.includes("close") && !!data?.is_resolved;
  const canResolve = statusActions.includes("resolve") && !data?.is_closed;
  const hasStatusAction = canReopen || canClose || canResolve;

  const timeline = useMemo(
    () => (data ? buildTimeline(data) : []),
    [data],
  );

  function jumpToReply() {
    replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // preventScroll so focusing doesn't snap past the smooth scroll we just
    // started.
    replyInputRef.current?.focus({ preventScroll: true });
  }

  async function submitNote() {
    if (!reply.trim() || busy) return;
    setBusy("note");
    setActionError(null);
    try {
      await api.tickets.addNote(ticketNumber, { body: reply.trim() });
      setReply("");
      await mutate();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to add reply.");
    } finally {
      setBusy(null);
    }
  }

  async function onAttach(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || busy) return;
    setBusy("attach");
    setActionError(null);
    try {
      // Same vetting as the new-ticket form: an oversize screenshot is shrunk
      // to fit Autotask's cap instead of being rejected by the backend (#191).
      const fitted = await fitUnderAttachCap(file);
      if (!fitted) {
        setActionError(
          `${file.name} is over the ${MAX_ATTACH_MB} MB limit. Please compress or split it.`,
        );
        return;
      }
      await api.tickets.uploadAttachment(ticketNumber, fitted);
      await mutate();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to upload attachment.",
      );
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  }

  async function downloadAttachment(a: AutotaskTicketAttachment) {
    if (a.id === null || downloadingId !== null) return;
    const id = a.id;
    const fallback = a.filename ?? a.title ?? `attachment-${id}`;
    setDownloadingId(id);
    setDownloadError(null);
    try {
      const { blob, filename } = await api.tickets.downloadAttachment(
        ticketNumber,
        id,
        fallback,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(
        e instanceof Error ? e.message : "Failed to download attachment.",
      );
    } finally {
      setDownloadingId(null);
    }
  }

  async function doStatus(action: "reopen" | "resolve" | "close") {
    if (busy) return;
    if (
      action === "close" &&
      !window.confirm(
        "Close this ticket? It will be marked Complete and can no longer be " +
          "reopened — you'd need to log a new ticket if the issue returns.",
      )
    ) {
      return;
    }
    setBusy(action);
    setActionError(null);
    try {
      await api.tickets.changeStatus(ticketNumber, action);
      await mutate();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to change status.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-panel modal-panel-lg" ref={panelRef}>
        <header className="modal-header">
          <div>
            <h2 className="modal-title">{data?.title ?? ticketNumber}</h2>
            <p className="modal-subtitle">
              {data?.ticketNumber ?? ticketNumber}
              {data?.status_label && (
                <>
                  {" "}
                  ·{" "}
                  <span className={statusBadgeClass(data.status_label)}>
                    {data.status_label}
                  </span>
                </>
              )}
              {data?.priority_label && <> · {data.priority_label}</>}
              {data?.created_at && <> · Opened {fmtDate(data.created_at)}</>}
              {data?.synced_at && <> · {syncedAgo(data.synced_at)}</>}
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal-body">
          {isLoading && <p>Loading ticket...</p>}
          {error && !isLoading && (
            <div className="form-error">
              {String((error as Error).message ?? error)}
            </div>
          )}

          {data && (
            <>
              {data.is_closed && !data.is_resolved && (
                <div className="info-banner warning">
                  <div>
                    <strong>This ticket is closed and can't be reopened.</strong>{" "}
                    If your issue has come back or you still need help, please log
                    a new ticket
                    {data.ticketNumber ? ` and mention ${data.ticketNumber}` : ""}.
                    {onNewTicket && (
                      <div style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={onNewTicket}
                        >
                          Log a new ticket
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="ticket-detail-meta">
                <div>
                  <div className="ticket-detail-meta-label">Company</div>
                  <div className="ticket-detail-meta-value">
                    {data.company_name ?? "—"}
                  </div>
                </div>
                <div>
                  <div className="ticket-detail-meta-label">Requester</div>
                  <div className="ticket-detail-meta-value">
                    {data.requester_name ?? "—"}
                  </div>
                </div>
                <div>
                  <div className="ticket-detail-meta-label">Assignee</div>
                  <div className="ticket-detail-meta-value">
                    {data.assignee_name ?? "Unassigned"}
                  </div>
                </div>
                <div>
                  <div className="ticket-detail-meta-label">Category</div>
                  <div className="ticket-detail-meta-value">
                    {data.category_label ?? "—"}
                  </div>
                </div>
                {/* The grid is a fixed two columns, so Age trails the pair of
                    due dates rather than splitting them across rows. */}
                <div>
                  <div className="ticket-detail-meta-label">
                    First response due
                  </div>
                  <div className="ticket-detail-meta-value">
                    {fmtDate(data.sla.first_response_due) || "—"}
                  </div>
                </div>
                <div>
                  <div className="ticket-detail-meta-label">Resolution due</div>
                  <div className="ticket-detail-meta-value">
                    {fmtDate(data.sla.resolved_due) || "—"}
                  </div>
                </div>
                <div>
                  <div className="ticket-detail-meta-label">Age</div>
                  <div className="ticket-detail-meta-value">
                    {fmtAge(data.age_hours)}
                  </div>
                </div>
              </div>

              {(hasStatusAction || canNote) && (
                <div className="ticket-actions">
                  {canNote && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={jumpToReply}
                    >
                      Add a reply
                    </button>
                  )}
                  {canReopen && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy !== null}
                      onClick={() => doStatus("reopen")}
                    >
                      {busy === "reopen" ? "Reopening..." : "Reopen ticket"}
                    </button>
                  )}
                  {canClose && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy !== null}
                      onClick={() => doStatus("close")}
                      title="Close this ticket now instead of waiting for it to close automatically."
                    >
                      {busy === "close" ? "Closing..." : "Close ticket"}
                    </button>
                  )}
                  {canResolve && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy !== null}
                      onClick={() => doStatus("resolve")}
                      title="Marks the ticket resolved. You'll have a few days to reopen it before it closes automatically."
                    >
                      {busy === "resolve" ? "Resolving..." : "Mark as resolved"}
                    </button>
                  )}
                </div>
              )}

              {data.is_resolved && statusActions.includes("reopen") && (
                <div className="info-banner info">
                  This ticket is resolved. If everything's sorted you can close
                  it now — otherwise reopen it and it'll go back to our team. It
                  closes automatically after 3 business days.
                </div>
              )}

              {data.description && (
                <div className="ticket-description">{data.description}</div>
              )}

              {data.csat_temperature ? (
                <div className="csat-panel">
                  <span className={`csat-panel-icon ${csatClass(data.csat_temperature)}`}>
                    {data.csat_temperature}
                  </span>
                  <div className="csat-panel-body">
                    Customer Thermometer rating
                    {data.csat_comment && (
                      <div className="csat-panel-comment">"{data.csat_comment}"</div>
                    )}
                  </div>
                </div>
              ) : (
                // Not yet rated: the requester can open the Customer Thermometer
                // survey on a Resolved or Complete ticket. CsatSurvey renders
                // nothing for every other viewer. Once a rating lands, Customer
                // Thermometer writes it back to the UDF and the block above (the
                // result) replaces this one.
                csatSurveyEligible(data) && <CsatSurvey ticket={data} />
              )}

              {data.attachments.length > 0 && (
                <>
                  <div className="ticket-notes-heading">
                    Attachments ({data.attachments.length})
                  </div>
                  <div className="ticket-attachments">
                    {data.attachments.map((a, i) => {
                      const meta = [
                        a.size_bytes ? fmtFileSize(a.size_bytes) : null,
                        a.created_by,
                        fmtDate(a.created_at) || null,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <button
                          type="button"
                          key={a.id ?? a.filename ?? `attachment-${i}`}
                          className="iq-file-row ticket-attachment-row"
                          disabled={a.id === null || downloadingId !== null}
                          onClick={() => void downloadAttachment(a)}
                          title={`Download ${a.filename ?? a.title ?? "attachment"}`}
                        >
                          <Icon
                            name="file"
                            size={16}
                            style={{ color: "var(--iq-violet)", flexShrink: 0 }}
                          />
                          <span className="ticket-attachment-name">
                            {a.filename ?? a.title ?? "Attachment"}
                          </span>
                          <span
                            className="row-meta"
                            style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                          >
                            {downloadingId === a.id ? "Downloading…" : meta}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {downloadError && (
                    <div className="form-error" style={{ marginTop: 8 }}>
                      {downloadError}
                    </div>
                  )}
                </>
              )}

              <div className="ticket-notes-heading">
                Followers ({data.followers.length})
              </div>
              <div className="ticket-thread">
                {data.followers.length === 0 && (
                  <p className="ticket-empty">No followers on this ticket.</p>
                )}
                {data.followers.map((f) => (
                  <div
                    key={f.resource_id}
                    className="ticket-thread-message ticket-thread-message-support"
                  >
                    <div className="ticket-thread-body">
                      <strong>{f.name ?? "Unknown contact"}</strong>
                      {f.role_label && (
                        <span className="row-meta" style={{ marginLeft: 8 }}>
                          {f.role_label}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="ticket-notes-heading">
                History ({timeline.length})
              </div>
              <div className="ttl">
                {timeline.length === 0 && (
                  <p className="ticket-empty">No activity on this ticket yet.</p>
                )}
                {timeline.map((item, i) => (
                  <div key={item.key} className="ttl-item">
                    <div className="ttl-rail">
                      <span
                        className={`ttl-dot${item.kind === "work" ? " work" : ""}`}
                      />
                      {i < timeline.length - 1 && <span className="ttl-line" />}
                    </div>
                    <div className="ttl-body">
                      <div className="ttl-meta">
                        <span
                          className={`ttl-tag${item.kind === "work" ? " work" : ""}`}
                        >
                          {item.kind === "work" ? "Work log" : "Note"}
                        </span>
                        {item.who} · {fmtDate(item.at)}
                        {item.kind === "work" && item.hours != null && (
                          <> · {item.hours}h</>
                        )}
                      </div>
                      <div className="ttl-text">
                        {item.title && <strong>{item.title}</strong>}
                        {item.title && item.body ? <br /> : null}
                        {item.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {(canNote || canAttach) && (
                <div className="ticket-reply" ref={replyRef}>
                  {canNote && (
                    <div className="form-group">
                      <label className="form-label" htmlFor="ticket-reply-body">
                        Add a reply
                      </label>
                      <textarea
                        id="ticket-reply-body"
                        ref={replyInputRef}
                        className="form-input"
                        rows={4}
                        maxLength={32_000}
                        value={reply}
                        disabled={busy !== null}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Reply to iQuest on this ticket..."
                      />
                    </div>
                  )}

                  {actionError && (
                    <div className="form-error">{actionError}</div>
                  )}

                  <div className="ticket-reply-actions">
                    {canNote && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={busy !== null || !reply.trim()}
                        onClick={submitNote}
                      >
                        {busy === "note" ? "Sending..." : "Send reply"}
                      </button>
                    )}
                    {canAttach && (
                      <label className="btn btn-secondary ticket-attach-btn">
                        {busy === "attach" ? "Uploading..." : "Attach file"}
                        <input
                          type="file"
                          hidden
                          disabled={busy !== null}
                          onChange={onAttach}
                        />
                      </label>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
